import {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  CheckCircleOutline as CheckCircleIcon,
  CloudUploadOutlined as CloudUploadIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  DescriptionOutlined as FileIcon,
  DownloadOutlined as DownloadIcon,
  ErrorOutline as ErrorIcon,
  InfoOutlined as InfoIcon,
  TableChartOutlined as TableIcon,
  UploadFileOutlined as UploadFileIcon
} from '@mui/icons-material';
import Loader from 'src/common/Loader';
import FMSModal from 'src/common/FMSModal';
import { APIService } from 'src/api/api';
import { getStoredUserTimezone } from 'src/utils/timezoneUtils';
import {
  downloadExternalTransactionTemplate,
  formatExternalTransactionDateTime,
  parseExternalTransactionWorkbook,
  validateExternalTransactionRows
} from './externalTransactionBulkUploadUtils';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';
const UPLOAD_STEPS = [
  { label: 'Upload File', description: 'Select Excel file' },
  { label: 'Validate Data', description: 'Check for errors' },
  { label: 'Confirm & Upload', description: 'Upload transactions' }
];

const getDispenserLabel = (dispenser) => (
  dispenser.station_name || dispenser.name || 'Unnamed dispenser'
);

const formatFileSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getErrorMessage = (response) => response?.data?.error_message
  || response?.error_message
  || response?.data?.detail
  || 'The workbook could not be uploaded.';

function ExternalTransactionBulkUpload({
  open, onClose, dispensers, sites, onComplete
}) {
  const fileInputRef = useRef(null);
  const [dispenserId, setDispenserId] = useState('');
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [reading, setReading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const selectedDispenser = useMemo(
    () => dispensers.find((dispenser) => String(dispenser.id) === String(dispenserId)),
    [dispenserId, dispensers]
  );
  const validRows = rows.filter((row) => row.status === 'ready');
  const invalidRows = rows.filter((row) => row.status === 'invalid');
  const selectedSite = sites.find((site) => String(site.id) === String(selectedDispenser?.site));
  const selectedTimezone = selectedSite?.timezone
    || selectedDispenser?.site_timezone
    || getStoredUserTimezone();

  let activeStep = 0;
  if (file) {
    activeStep = 1;
    if (!reading && rows.length && !invalidRows.length && validRows.length) activeStep = 2;
  }

  useEffect(() => {
    if (!open) {
      setDispenserId('');
      setFile(null);
      setRows([]);
      setParseError('');
      setUploadError('');
      setReading(false);
      setUploading(false);
      setDragOver(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !dispensers.length) return;

    const hasSelectedDispenser = dispensers.some(
      (dispenser) => String(dispenser.id) === String(dispenserId)
    );
    if (!hasSelectedDispenser) {
      setDispenserId(dispensers.length === 1 ? String(dispensers[0].id) : '');
    }
  }, [dispenserId, dispensers, open]);

  useEffect(() => {
    if (rows.length) setRows((currentRows) => validateExternalTransactionRows(currentRows, selectedTimezone));
  }, [selectedTimezone]);

  const clearFile = () => {
    setFile(null);
    setRows([]);
    setParseError('');
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const processFile = async (nextFile) => {
    if (!nextFile) return;

    setDragOver(false);
    setUploadError('');
    setParseError('');
    setRows([]);

    if (nextFile.size > MAX_FILE_SIZE_BYTES) {
      setFile(null);
      setParseError('File size must be 10 MB or less.');
      return;
    }

    setFile(nextFile);
    setReading(true);
    try {
      const parsedRows = await parseExternalTransactionWorkbook(nextFile);
      setRows(validateExternalTransactionRows(parsedRows, selectedTimezone));
    } catch (error) {
      setParseError(error.message || 'The workbook could not be read.');
    } finally {
      setReading(false);
    }
  };

  const handleFileChange = async (event) => {
    const input = event.target;
    const nextFile = input.files?.[0];
    input.value = '';
    await processFile(nextFile);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    if (uploading || reading) return;
    await processFile(event.dataTransfer.files?.[0]);
  };

  const handleUpload = async () => {
    if (!file || !dispenserId || invalidRows.length || !validRows.length) return;
    setUploading(true);
    setUploadError('');
    try {
      const response = await APIService.bulkUploadExternalTransactions(file, dispenserId);
      if (!response?.success) throw response;
      onComplete(response.result);
      onClose();
    } catch (error) {
      setUploadError(getErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  return (
    <FMSModal open={open} onClose={uploading ? undefined : onClose} maxWidth="lg">
      <DialogTitle
        sx={{
          p: { xs: 1.5, sm: 2 },
          pb: { xs: 2, sm: 2.5 },
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: 'primary.main',
              bgcolor: 'primary.50'
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800}>Upload External Transactions</Typography>
            <Typography variant="body2" color="text.secondary">
              Upload today&apos;s or yesterday&apos;s transactions. Every row must contain an exact vehicle number match.
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} disabled={uploading} aria-label="Close upload dialog">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: { xs: 1.5, sm: 2 },
          pt: { xs: 2.5, sm: 3 },
          minHeight: 0,
          maxHeight: { xs: 'calc(100vh - 125px)', sm: 'calc(90vh - 125px)' },
          overflowY: 'auto'
        }}
      >
        <Stack spacing={{ xs: 2, sm: 2.5 }}>
          <Box
            sx={{
              mt: 0.5,
              px: { xs: 1, sm: 1.5 },
              pt: { xs: 1.5, sm: 2 },
              pb: { xs: 1, sm: 1.5 },
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.5,
              bgcolor: 'grey.50'
            }}
          >
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                '& .MuiStepLabel-label': { fontWeight: 700 },
                '& .MuiStepLabel-labelContainer': { mt: 0.5 },
                '& .MuiStepConnector-line': { borderColor: 'divider' }
              }}
            >
              {UPLOAD_STEPS.map((step) => (
                <Step key={step.label}>
                  <StepLabel>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>{step.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{step.description}</Typography>
                    </Box>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={8}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  if (!uploading && !reading) setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                sx={{
                  minHeight: 160,
                  p: { xs: 1.5, sm: 2.5 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  cursor: uploading || reading ? 'default' : 'pointer',
                  border: 2,
                  borderStyle: 'dashed',
                  borderColor: dragOver ? 'primary.main' : 'primary.200',
                  borderRadius: 2,
                  bgcolor: dragOver ? 'primary.50' : 'grey.50',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: uploading || reading ? 'primary.200' : 'primary.main',
                    bgcolor: uploading || reading ? 'grey.50' : 'primary.50'
                  }
                }}
              >
                <input
                  ref={fileInputRef}
                  hidden
                  type="file"
                  accept={ACCEPTED_FILE_TYPES}
                  onChange={handleFileChange}
                  disabled={uploading || reading}
                />
                <Stack spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      display: 'grid',
                      placeItems: 'center',
                      borderRadius: 2,
                      bgcolor: 'white',
                      color: 'primary.main',
                      boxShadow: 1
                    }}
                  >
                    <FileIcon sx={{ fontSize: 42 }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={800}>Choose Excel file to upload</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Drag and drop your Excel file here, or click to browse
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<UploadFileIcon />}
                    disabled={uploading || reading}
                    onClick={(event) => {
                      event.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    sx={{
                      textTransform: 'none',
                      px: 2,
                      py: 0.8,
                      fontWeight: 700
                    }}
                  >
                    Choose Excel file
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Supported format: .xlsx, .xls&nbsp;&nbsp;|&nbsp;&nbsp;Max file size: 10 MB
                  </Typography>
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={1.5} height="100%">
                <Box
                  sx={{
                    flex: 1,
                    p: 1.75,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <DownloadIcon color="primary" sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={800}>Template</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Download the Excel template with required columns and format.
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={downloadExternalTransactionTemplate}
                    disabled={uploading}
                    sx={{
                      mt: 1.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      py: 0.8
                    }}
                  >
                    Download Excel template
                  </Button>
                </Box>

                <FormControl fullWidth required disabled={uploading}>
                  <InputLabel id="external-upload-dispenser-label">Dispenser</InputLabel>
                  <Select
                    labelId="external-upload-dispenser-label"
                    value={dispenserId}
                    label="Dispenser"
                    onChange={(event) => setDispenserId(event.target.value)}
                  >
                    {dispensers.map((dispenser) => (
                      <MenuItem key={dispenser.id} value={dispenser.id}>
                        {getDispenserLabel(dispenser)}
                      </MenuItem>
                    ))}
                  </Select>
                  {dispensers.length > 1 && !dispenserId && (
                    <FormHelperText>Select a dispenser to enable upload.</FormHelperText>
                  )}
                </FormControl>
              </Stack>
            </Grid>
          </Grid>

          {file && (
            <Box
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                border: 1,
                borderColor: 'success.200',
                borderRadius: 2,
                bgcolor: 'success.50'
              }}
            >
              <CheckCircleIcon color="success" sx={{ fontSize: 34 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography fontWeight={800}>Selected file</Typography>
                <Typography color="text.secondary" noWrap title={file.name}>
                  {file.name}
                  {file.size ? ` (${formatFileSize(file.size)})` : ''}
                </Typography>
              </Box>
              <IconButton onClick={clearFile} disabled={uploading} aria-label="Remove selected file">
                <DeleteIcon />
              </IconButton>
            </Box>
          )}

          {reading && <Loader />}
          {parseError && <Alert severity="error">{parseError}</Alert>}
          {uploadError && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{uploadError}</Alert>}

          {rows.length > 0 && (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={1.5}
                sx={{ p: 1.5, bgcolor: 'grey.50' }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <TableIcon color="primary" sx={{ fontSize: 28 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={800}>Workbook Preview</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Review the data from your Excel file before uploading.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`${validRows.length} valid rows`}
                    color="success"
                    variant="outlined"
                  />
                  <Chip
                    icon={<ErrorIcon />}
                    label={`${invalidRows.length} invalid row${invalidRows.length === 1 ? '' : 's'}`}
                    color="error"
                    variant="outlined"
                  />
                </Stack>
              </Stack>

              <TableContainer sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small" sx={{ minWidth: 850 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Vehicle Number</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Date and Time</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => {
                      const isInvalid = row.status === 'invalid';
                      return (
                        <TableRow key={row.rowNumber} sx={isInvalid ? { bgcolor: 'error.50' } : undefined}>
                          <TableCell>{row.rowNumber}</TableCell>
                          <TableCell>{row.values.vehicleNumber || '—'}</TableCell>
                          <TableCell>{row.values.quantity || '—'}</TableCell>
                          <TableCell>
                            {formatExternalTransactionDateTime(row.values.dateTime, selectedTimezone) || '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              icon={isInvalid ? <ErrorIcon /> : <CheckCircleIcon />}
                              label={isInvalid ? 'Invalid' : 'Ready'}
                              color={isInvalid ? 'error' : 'success'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color={isInvalid ? 'error.main' : 'text.secondary'}>
                              {isInvalid ? row.errors.join('; ') : 'Ready to upload'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Box
            sx={{
              p: 1.5,
              border: 1,
              borderColor: 'info.200',
              borderRadius: 2,
              bgcolor: 'info.50'
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <InfoIcon color="info" sx={{ fontSize: 24 }} />
              <Box>
                <Typography fontWeight={800} color="info.dark">Important Notes</Typography>
                <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  <span>- Every row must contain an exact vehicle number match.</span>
                  <br />
                  <span>- Date and Time should be today or yesterday.</span>
                  <br />
                  <span>- Please ensure the file follows the required template format.</span>
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={uploading} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={handleUpload}
          disabled={uploading || reading || !dispenserId || !validRows.length || invalidRows.length > 0}
          sx={{ textTransform: 'none', fontWeight: 700, px: 2 }}
        >
          {uploading ? 'Uploading…' : 'Upload Transactions'}
        </Button>
      </DialogActions>
    </FMSModal>
  );
}

ExternalTransactionBulkUpload.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dispensers: PropTypes.arrayOf(PropTypes.object).isRequired,
  sites: PropTypes.arrayOf(PropTypes.object).isRequired,
  onComplete: PropTypes.func.isRequired
};

export default ExternalTransactionBulkUpload;
